import { MinusCircleIcon, PlusCircleIcon } from "lucide-react";
import React from "react";
import { generateUID } from "verdi-common/utils/index";
import { SIZE } from "@/configs/sizes";
import { Icon } from "../icon";
import { Button } from "../ui/button";
import { Field, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { InputGroup } from "../ui/input-group";

export type FormMultiInputGroupProps = {
    name: string;
    newValueId?: string;
    newValuePlaceholder: string;
    defaultValues: string[];
    fieldLabel?: string;
    preventDuplicates?: boolean; // if true, the form will be invalid if the user tries to add a duplicate value
    required?: boolean; // if true, the form will be invalid if the list is empty
};

const DUPLICATE_ERROR_MESSAGE = "Duplicate values are not allowed";
const EMPTY_VALUE_ERROR_MESSAGE = "Values cannot be empty";
const VALUE_REQUIRED_ERROR_MESSAGE = "At least one value is required";

// Used to identify the new value before it's added to the list
const NEW_VALUE_ID = generateUID();

/*
 * A component that allows the user to create a list of values, with the ability to add and remove them
 * Requires a parent <Form> component for submitting the data
 */
export function FormMultiSelectGroup({
    name,
    newValueId,
    newValuePlaceholder,
    defaultValues,
    fieldLabel,
    preventDuplicates = false,
    required = true,
}: FormMultiInputGroupProps) {
    const [values, setValues] = React.useState<Array<{ id: string; value: string }>>(() =>
        defaultValues.map((value) => ({ id: generateUID(), value })),
    );
    const [newInputValue, setNewInputValue] = React.useState("");
    const [errorStateById, setErrorStateById] = React.useState<Record<string, boolean>>({});
    const [errorMessages, setErrorMessages] = React.useState<Set<string>>(new Set());

    const validateValues = (
        newValueArray: Array<{ id: string; value: string }>,
    ): { newErrorMessages: Set<string>; newErrorStateById: Record<string, boolean> } => {
        if (required && newValueArray.length === 0) {
            const newErrorMessages = new Set([VALUE_REQUIRED_ERROR_MESSAGE]);
            const newErrorStateById = { [NEW_VALUE_ID]: true };

            setErrorMessages(newErrorMessages);
            setErrorStateById(newErrorStateById);
            return { newErrorMessages, newErrorStateById };
        }

        const newErrorMessages: Set<string> = new Set();
        const valueCounts = new Map<string, number>();

        // count occurrences of each value
        newValueArray.forEach((valueObject) => {
            const value = valueObject.value;
            if (value.length > 0) {
                valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
            }
        });

        // Map each value to its error state: true if duplicate or empty, false otherwise
        const newErrorStateById: Record<string, boolean> = {};
        newValueArray.forEach((valueObject) => {
            const value = valueObject.value;
            if (preventDuplicates && value && (valueCounts.get(value) || 0) > 1) {
                newErrorStateById[valueObject.id] = true;
                newErrorMessages.add(DUPLICATE_ERROR_MESSAGE);
            } else if (value.length === 0) {
                newErrorStateById[valueObject.id] = true;
                newErrorMessages.add(EMPTY_VALUE_ERROR_MESSAGE);
            } else {
                newErrorStateById[valueObject.id] = false;
            }
        });

        setErrorMessages(newErrorMessages);
        setErrorStateById(newErrorStateById);

        return { newErrorMessages, newErrorStateById };
    };

    const onInputChange = (id: string, newValue: string) => {
        setValues((prev) => prev.map((item) => (item.id === id ? { ...item, value: newValue } : item)));
    };

    const removeValue = (idToRemove: string) => {
        const newValues = values.filter((item) => item.id !== idToRemove);
        setValues(newValues);
        validateValues(newValues);
    };

    const onNewInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setNewInputValue(inputValue);
    };

    const addValue = () => {
        const inputValue = newInputValue.trim();

        // validate new state using the sentinel id before adding the new value to the list
        const { newErrorStateById } = validateValues([...values, { id: NEW_VALUE_ID, value: inputValue }]);

        // if the new value is invalid, don't add it to the list
        if (newErrorStateById[NEW_VALUE_ID]) {
            return;
        }
        setValues([...values, { id: generateUID(), value: inputValue }]);
        setNewInputValue("");
    };

    return (
        <Field className={"grow"}>
            <FieldLabel>{fieldLabel}</FieldLabel>
            <div className={"w-full flex flex-col gap-2"}>
                <div className={"flex flex-col gap-2"}>
                    {values.length !== 0 ? (
                        values.map((item) => (
                            <Field key={item.id} className={"nth-last-2:mb-3"} invalid={errorStateById[item.id]}>
                                <InputGroup>
                                    <Input
                                        type={"text"}
                                        name={name}
                                        value={item.value}
                                        onChange={(e) => onInputChange(item.id, e.target.value)}
                                        onBlur={(e) => {
                                            // trim the value and update the values list, then validate the new values
                                            const trimmedValue = e.target.value.trim();
                                            const updatedValues = values.map((prev) =>
                                                prev.id === item.id ? { ...prev, value: trimmedValue } : prev,
                                            );

                                            setValues(updatedValues);
                                            validateValues(updatedValues);
                                        }}
                                    />
                                    <Button
                                        type={"button"}
                                        variant={"ghost"}
                                        onClick={() => removeValue(item.id)}
                                        tooltip={"Delete Value"}
                                    >
                                        <Icon
                                            icon={MinusCircleIcon}
                                            size={SIZE.SM}
                                            iconClassName={"stroke-destructive"}
                                            renderAs={<div />}
                                        />
                                    </Button>
                                </InputGroup>
                            </Field>
                        ))
                    ) : (
                        <div
                            className={"text-[0.75em] pt-[10px] pb-[16px] text-center text-muted-foreground"}
                        >{`No ${fieldLabel || "values"} set`}</div>
                    )}
                    <Field className={"grow"} invalid={errorStateById[NEW_VALUE_ID]}>
                        <InputGroup>
                            <Input
                                // ignored when submitting form due to no "name" prop
                                required={required && values.length === 0}
                                id={newValueId}
                                type={"text"}
                                placeholder={newValuePlaceholder}
                                value={newInputValue}
                                onChange={onNewInputChange}
                                onBlur={(e) => {
                                    // onBlur only validates when the values list is empty to prevent case where newInputValue is not added to the list and is the only value
                                    if (values.length === 0) {
                                        validateValues(values);
                                    }
                                    setNewInputValue(e.target.value.trim());
                                }}
                            />
                            <Button type={"button"} variant={"ghost"} onClick={() => addValue()} tooltip={"Add Value"}>
                                <Icon
                                    icon={PlusCircleIcon}
                                    size={SIZE.SM}
                                    iconClassName={"stroke-success"}
                                    renderAs={<div />}
                                />
                            </Button>
                        </InputGroup>
                    </Field>
                </div>
                {[...errorMessages].map((errorMessage) => (
                    <div key={errorMessage} className={"text-destructive"}>
                        {errorMessage}
                    </div>
                ))}
            </div>
        </Field>
    );
}
